import React, { useEffect, useState } from "react";
import { getToken, base_url } from "../../config/config";
import { X, Eye, EyeOff, Pencil } from "lucide-react";

const UserProfile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showPreviousPassword, setShowPreviousPassword] = useState(false);
  const [previousPassword, setPreviousPassword] = useState("");

  const userTypeOptions = ["super-admin", "admin", "user"];

  const fetchLoggedInUser = async () => {
    try {
      const response = await fetch(`${base_url}/v1/GetUser`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error("Unauthorized or failed to fetch user");
      }

      const data = await response.json();
      setUser(data.data);
    } catch (err) {
      setError(err.message || "Failed to fetch user information");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLoggedInUser();
  }, []);

  const handleEditCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setShowPassword(false);
    setShowPreviousPassword(false);
    setPreviousPassword("");
  };

  const handleSave = async () => {
    try {
      const response = await fetch(
        `${base_url}/v1/UpdateUser?id=${selectedUser._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify({
            firstName: selectedUser.firstName,
            lastName: selectedUser.lastName,
            email: selectedUser.email,
            userType: selectedUser.userType,
            // Only include password if it was changed, and include previous password for verification
            ...(selectedUser.password && {
              password: selectedUser.password,
              previousPassword: previousPassword,
            }),
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        alert("Benutzer erfolgreich bearbeitet!");
        handleEditCloseDialog();
        fetchLoggedInUser();
      } else {
        alert(data.message || "Fehler beim Bearbeiten des Benutzers.");
      }
    } catch (error) {
      console.error("Error updating user:", error);
    }
  };

  if (error) return <div className="p-4 text-red-600 text-center">{error}</div>;

  return (
    <div>
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="max-w-md mx-auto mt-10 p-6 shadow-xl rounded-xl border border-gray-100 relative">
          {/* Edit button at top-right */}
          <button
            onClick={() => {
              setSelectedUser({ ...user, password: "" }); // initialize password
              setPreviousPassword(""); // initialize previous password
              setOpenDialog(true);
            }}
            className="absolute top-4 right-4 p-2 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-all duration-200 shadow-md"
          >
            <Pencil className="h-4 w-4" />
          </button>

          {/* Profile card */}
          <div className="flex flex-col items-center text-center animate-fade-in">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-100 to-blue-300 text-blue-700 flex items-center justify-center text-3xl font-extrabold mb-4 shadow-md">
              {user?.firstName?.charAt(0)}
              {user?.lastName?.charAt(0)}
            </div>
            <h2 className="text-2xl font-semibold text-gray-800">
              {user?.firstName} {user?.lastName}
            </h2>
            <p className="text-gray-500">{user?.email}</p>

            <div className="mt-8 flex flex-wrap justify-center gap-2">
              <span className="px-3 py-1 text-xs font-medium bg-blue-500 text-white rounded-full shadow">
                {user?.userType}
              </span>
            </div>
            <div className="mt-8 flex flex-wrap justify-center gap-2">
              <span className="px-3 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full border border-green-200">
                Erstellt: {new Date(user?.createdAt).toLocaleDateString()}
              </span>
              <span className="px-3 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full border border-yellow-200">
                Aktualisiert: {new Date(user?.updatedAt).toLocaleDateString()}
              </span>
            </div>
          </div>

          {/* Dialog */}
          {openDialog && selectedUser && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-900">
                      Benutzer bearbeiten
                    </h2>
                    <button
                      onClick={handleEditCloseDialog}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Vorname
                      </label>
                      <input
                        type="text"
                        value={selectedUser.firstName}
                        onChange={(e) =>
                          setSelectedUser({
                            ...selectedUser,
                            firstName: e.target.value,
                          })
                        }
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Nachname
                      </label>
                      <input
                        type="text"
                        value={selectedUser.lastName}
                        onChange={(e) =>
                          setSelectedUser({
                            ...selectedUser,
                            lastName: e.target.value,
                          })
                        }
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      E-mail
                    </label>
                    <input
                      type="email"
                      value={selectedUser.email}
                      onChange={(e) =>
                        setSelectedUser({
                          ...selectedUser,
                          email: e.target.value,
                        })
                      }
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    />
                  </div>
                  {/* Previous Password Field - Only show when editing user and changing password */}
                  {selectedUser && selectedUser.password && (
                    <div className="relative space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Aktuelles Passwort des Benutzers bestätigen
                      </label>
                      <div className="relative">
                        <input
                          type={showPreviousPassword ? "text" : "password"}
                          value={previousPassword}
                          onChange={(e) => setPreviousPassword(e.target.value)}
                          placeholder="Aktuelles Passwort des Benutzers eingeben"
                          autoComplete="current-password"
                          className="w-full rounded-lg border border-gray-300 px-4 py-2 pr-10 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
                          onClick={() =>
                            setShowPreviousPassword(!showPreviousPassword)
                          }
                        >
                          {showPreviousPassword ? (
                            <EyeOff size={20} />
                          ) : (
                            <Eye size={20} />
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="relative space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Neues Passwort (leer lassen, um nicht zu ändern)
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={selectedUser.password || ""}
                        onChange={(e) =>
                          setSelectedUser({
                            ...selectedUser,
                            password: e.target.value,
                          })
                        }
                        autoComplete="new-password"
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 pr-10 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff size={20} />
                        ) : (
                          <Eye size={20} />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Benutzertyp
                    </label>
                    <select
                      value={selectedUser.userType}
                      onChange={(e) =>
                        setSelectedUser({
                          ...selectedUser,
                          userType: e.target.value,
                        })
                      }
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    >
                      {userTypeOptions.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                  <button
                    onClick={handleEditCloseDialog}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                  >
                    Speichern
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserProfile;
